import { currentSubSession } from './currentSubSession';
import {
  isSameOriginUrl,
  readSubSessionIdFromSearch,
  searchWithSubSessionId,
} from './subSessionUrl';

const URL_PARTS = /^([^?#]*)(\?[^#]*)?(#[\s\S]*)?$/;

type WithSubSessionParamOptions = {
  /** A backend URL (e.g. a redirect) keeps an `ibe.ss` it already carries: that is IBE's decision. */
  isBackendUrl?: boolean;
};

/**
 * Adds this tab's confirmed sub-session id as `ibe.ss` to full-page navigations,
 * redirects and form posts that stay in this tab, since a service worker cannot
 * tag navigations. The rest of the URL is kept byte for byte, and the id never
 * goes to another origin.
 *
 * Not for rendered links or booking pages opened in a new tab: a new tab must
 * get a sub-session of its own. A backend document in a new tab, like the
 * printed receipt, may carry it.
 */
export const withSubSessionParam = (
  url: string,
  { isBackendUrl = false }: WithSubSessionParamOptions = {},
): string => {
  const subSessionId = currentSubSession();
  const parts = URL_PARTS.exec(url);
  if (!subSessionId || !parts || !isSameOriginUrl(url)) {
    return url;
  }

  const [, beforeSearch, search = '', hash = ''] = parts;
  // A hash-only or empty URL has no path: adding a query would drop the current one.
  if (!beforeSearch && !search) {
    return url;
  }
  if (isBackendUrl && readSubSessionIdFromSearch(search) !== null) {
    return url;
  }

  return `${beforeSearch}${searchWithSubSessionId(search, subSessionId)}${hash}`;
};
